import React, { Fragment } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { Typography, Tooltip } from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import classNames from 'classnames';
import { ToCurrencyString, ToCurrencyStringWithDecimals, ToPercentageString, ToPercentageStringWithDecimals } from 'services/formatService';
import SentimentVeryDissatisfiedIcon from '@material-ui/icons/SentimentVeryDissatisfied';
import { Constants } from 'utilities/constants';

const PropertyUWProformaSell = ({ classes, valuationOutput, shouldCalculateProforma, selectedOpportunity }) => {
  const canDisplay = number => !(number == null || isNaN(number) || number === 0);

  const GetCurrencyString = (number, isNegative = false, fontBold = false) => {
    return (<Typography className={classNames(classes.valueSpacing, classes.tabContent, (isNegative ? classes.fontRed : null), (fontBold ? classes.fontBold : null))} variant='body2'>
      {canDisplay(number) ? (isNegative ? `(${ToCurrencyString(Math.abs(number))})` : `${ToCurrencyString(number)}`) : '-'}
    </Typography>)
  }

  return (
    <div style={{ opacity: shouldCalculateProforma ? 0.4 : 1 }}>
      {(valuationOutput.isSuccess && !valuationOutput.sellAssumptionBestFitIcbmOrderId) ?
        // If calculation success and there is no matching ICBM order then we hide detail output contents and show no matching message instead  
        <div className={classes.noContentPaper}>
          <SentimentVeryDissatisfiedIcon color='disabled' className={classes.noMatchingErrorIcon} />
          <Typography variant='subtitle1' color='textSecondary'>No matching ICBM order for retail</Typography>
        </div>
        :
        // Detailed output items
        <>
          <Typography variant='subtitle2' align='left'>{'EXIT VALUE'}</Typography>
          <div className={classes.groupContainer}>
            {/* ARV */}
            <div className={classes.proformaSpacing}>
              <Tooltip title="After Repair Value">
                <Typography variant='body2' className={classNames(classes.tabContent)}>{'ARV'}</Typography>
              </Tooltip>
              <Typography className={classes.tabSubContent} variant='body2'>
                {canDisplay(valuationOutput.afterRepairValuePerSqft) ? `${ToCurrencyStringWithDecimals(valuationOutput.afterRepairValuePerSqft)}/sqft` : null}
              </Typography>
              {GetCurrencyString(valuationOutput.afterRepairValue, false, true)}
            </div>
            {/* Broker Fees */}
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classes.tabContent}>{'Broker Fees'}</Typography>
              {GetCurrencyString(valuationOutput.brokerFee, true)}
            </div>
            {/* Friction Costs */}
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classes.tabContent}>{'Friction Costs'}</Typography>
              {GetCurrencyString(valuationOutput.frictionCost, true)}
            </div>
          </div>
          <Divider variant='middle' />
          <div className={classes.proformaSpacing}>
            <Typography variant='body2' className={classNames(classes.fontBold, classes.tabContent)}>
              {'Net Exit Value'}
            </Typography>
            {GetCurrencyString(valuationOutput.netExitValue, false, true)}
          </div>
          <div className={classes.proformaSpacing}>
            <Typography variant='body2' className={classNames(classes.tabContent)}>
              {'Bid Price'}
            </Typography>
            <Typography className={classes.tabSubContent} variant='body2'>
              {canDisplay(valuationOutput.bidPricePctOfAskingPrice) ? `${ToPercentageString(valuationOutput.bidPricePctOfAskingPrice)} of List` : null}
            </Typography>
            {GetCurrencyString(valuationOutput.bidPrice, false, false)}
          </div>
          <div className={classes.proformaSpacing}>
            <Typography variant='body2' className={classNames(classes.tabContent)}>
              {'Max Bid Price'}
            </Typography>
            <Typography className={classes.tabSubContent} variant='body2'>
              {(canDisplay(selectedOpportunity.askingPrice) && canDisplay(valuationOutput.sellAssumptionBid)) ? `${ToPercentageString(valuationOutput.sellAssumptionBid / selectedOpportunity.askingPrice)} of List` : null}
            </Typography>
            {GetCurrencyString(valuationOutput.sellAssumptionBid, false, false)}
          </div>
          {
            selectedOpportunity.channelText === Constants.Channel.AUCTION &&
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classNames(classes.tabContent)}>Min Net Margin</Typography>
              <Typography variant='body2' className={classNames(classes.valueSpacing, classes.tabContent)}>
                {canDisplay(valuationOutput.minNetMargin) ? ToPercentageString(valuationOutput.minNetMargin) : '-'}
              </Typography>
            </div>
          }

          <Typography variant='subtitle2' align='left'>{'COST BASIS'}</Typography>
          <div className={classes.groupContainer}>
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classNames(classes.fontBold, classes.tabContent)}>
                {'Retail Cost Basis'}
              </Typography>
              <Typography className={classes.tabSubContent} variant='body2'>
                {valuationOutput.sellCostBasis && valuationOutput.afterRepairValue && valuationOutput.afterRepairValue !== 0 ? `${ToPercentageStringWithDecimals(valuationOutput.sellCostBasis / valuationOutput.afterRepairValue, 1)} ARV` : null}
              </Typography>
              {GetCurrencyString(valuationOutput.sellCostBasis, false, true)}
            </div>
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classes.tabContent}>{'Carry Costs'}</Typography>
              {GetCurrencyString(valuationOutput.sellCarryCost, true)}
            </div>
            {/* Repairs */}
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classes.tabContent}>{'Repairs'}</Typography>
              <Typography className={classes.tabSubContent} variant='body2'>
                {valuationOutput.sellAssumptionRepairEstimatePerSqft ? `${ToCurrencyStringWithDecimals(valuationOutput.sellAssumptionRepairEstimatePerSqft)}/sqft` : null}
              </Typography>
              {GetCurrencyString(valuationOutput.sellAssumptionRepairEstimate, true)}
            </div>
            {/* Acquisition Fees */}
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classes.tabContent}>{'Acquisition Fees'}</Typography>
              {GetCurrencyString(valuationOutput.sellAcquisitionFees, true)}
            </div>
            {/* Closing Costs */}
            <div className={classes.proformaSpacing}>
              <Typography variant='body2' className={classes.tabContent}>{'Closing Costs'}</Typography>
              {GetCurrencyString(valuationOutput.sellClosingCosts, true)}
            </div>
            {
              selectedOpportunity.channelText === Constants.Channel.AUCTION &&
              <Fragment>
                <div className={classes.proformaSpacing}>
                  <Typography variant='body2' className={classes.tabContent}>{'Delinquent Taxes'}</Typography>
                  {GetCurrencyString(valuationOutput.delinquentTaxes, true)}
                </div>
                <div className={classes.proformaSpacing}>
                  <Typography variant='body2' className={classes.tabContent}>{'Liens'}</Typography>
                  {GetCurrencyString(valuationOutput.liens, true)}
                </div>
              </Fragment>
            }
          </div>
          <Divider variant='middle' />

          {selectedOpportunity.channelText === Constants.Channel.AUCTION ?
            <div className={classes.proformaSpacing}>
              <Typography variant='body1' className={classNames(classes.fontBold, classes.tabContent)}>{'Max Bid'}</Typography>
              <Typography className={classes.tabSubContent} variant='body2'>
                {(canDisplay(selectedOpportunity.askingPrice) && canDisplay(valuationOutput.sellAssumptionBid)) ? `${ToPercentageString(valuationOutput.sellAssumptionBid / selectedOpportunity.askingPrice)} of List` : null}
              </Typography>
              <Typography variant='body1' className={classNames(classes.valueSpacing, classes.fontBold, classes.tabContent)}>
                {valuationOutput.sellAssumptionBid ? ToCurrencyString(valuationOutput.sellAssumptionBid) : '-'}
              </Typography>
            </div>
            :
            <Fragment>
              {/* Net Margin  */}
              <div className={classes.proformaSpacing}>
                <Tooltip title='Net Gain'>
                  <Typography variant='body2' className={classNames(classes.fontBold, classes.tabContent)}>Net Gain</Typography>
                </Tooltip>
                <Typography variant='body2' className={classNames(classes.fontBold, classes.valueSpacing, classes.tabContent)}>
                  {canDisplay(valuationOutput.netGain) ? ToCurrencyString(valuationOutput.netGain) : '-'}
                </Typography>
              </div>
              {/* Net Margin  */}
              <div className={classes.proformaSpacing}>
                <Tooltip title='Net Margin'>
                  <Typography variant='body2' className={classNames(classes.fontBold, classes.tabContent)}>Net Margin</Typography>
                </Tooltip>
                <Typography variant='body2' className={classNames(classes.fontBold, classes.valueSpacing, classes.tabContent)}>
                  {canDisplay(valuationOutput.netMargin) ? ToPercentageString(valuationOutput.netMargin) : '-'}
                </Typography>
              </div>
              {/* PctOfMinNetMargin  */}
              {/* 
              <div className={classes.proformaSpacing}>
                <Tooltip title='Pct Of Min Net Margin'>
                  <Typography variant='body2' className={classNames(classes.fontBold, classes.tabContent)}>Pct Of Min Net Margin</Typography>
                </Tooltip>
                <Typography variant='body2' className={classNames(classes.fontBold, classes.valueSpacing, classes.tabContent)}>
                  {canDisplay(valuationOutput.pctOfMinNetMargin) ? ToPercentageString(valuationOutput.pctOfMinNetMargin) : '-'}
                </Typography>
              </div> 
              */}
            </Fragment>
          }
        </>
      }
    </div>
  )
};

const styles = theme => ({
  noContentPaper: {
    minHeight: '500px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center'
  },
  noMatchingErrorIcon: {
    fontSize: '2rem'
  },
  tabHeader: {
    fontSize: '14px',
  },
  vAlign: {
    display: 'flex',
    alignItems: 'center',
  },
  tabContent: {
    fontSize: '12px',
  },
  tabSubContent: {
    flexGrow: '1',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingRight: '10px',
    fontSize: '0.6rem',
    color: 'gray',
  },
  proformaSpacing: {
    display: 'flex',
    width: '100%',
    padding: '0 10px',
  },
  fontBold: {
    fontWeight: 'bold',
  },
  fontRed: {
    color: theme.palette.red.main,
  },
  valueSpacing: {
    marginLeft: 'auto',
  },
  groupContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'white',
  },
  // warningIcon: {
  //   color: theme.palette.red.main,
  //   fontSize: '1.25rem',
  // }
})
export default withStyles(styles)(PropertyUWProformaSell)












